import React, { useMemo, useState } from 'react';

/**
 * Local TasticProps type
 * This avoids dependency resolution issues on Netlify
 */
type TasticProps = {
    data?: Record<string, any>;
};

const ProWhipProductList = ({ data }: TasticProps) => {
    // 🔎 RAW DATA FROM STUDIO
    const productListBlock = data?.['Product List'];
    const dataSource = productListBlock?.dataSource;

    const products = dataSource?.items ?? [];
    const facets = dataSource?.facets ?? [];

    // -----------------------------
    // STATE
    // -----------------------------
    const [sort, setSort] = useState<
        'name-asc' | 'name-desc' | 'price-asc' | 'price-desc'
    >('name-asc');

    const [activeFilters, setActiveFilters] = useState<
        Record<string, string[]>
    >({});

    // -----------------------------
    // FILTER HANDLER
    // -----------------------------
    const toggleFilter = (facetKey: string, value: string) => {
        setActiveFilters((prev) => {
            const current = prev[facetKey] || [];
            return {
                ...prev,
                [facetKey]: current.includes(value)
                    ? current.filter((v) => v !== value)
                    : [...current, value],
            };
        });
    };

    // -----------------------------
    // SORT PRODUCTS
    // -----------------------------
    const sortedProducts = useMemo(() => {
        return [...products].sort((a: any, b: any) => {
            const aVariant = a.variants?.[0];
            const bVariant = b.variants?.[0];

            switch (sort) {
                case 'name-asc':
                    return a.name.localeCompare(b.name);
                case 'name-desc':
                    return b.name.localeCompare(a.name);
                case 'price-asc':
                    return (
                        (aVariant?.price?.centAmount ?? 0) -
                        (bVariant?.price?.centAmount ?? 0)
                    );
                case 'price-desc':
                    return (
                        (bVariant?.price?.centAmount ?? 0) -
                        (aVariant?.price?.centAmount ?? 0)
                    );
                default:
                    return 0;
            }
        });
    }, [products, sort]);

    // -----------------------------
    // APPLY FILTERS
    // -----------------------------
    const filteredProducts = useMemo(() => {
        return sortedProducts.filter((product: any) => {
            return Object.entries(activeFilters).every(
                ([facetKey, values]) => {
                    if (!values.length) return true;

                    const attrs = product.variants?.[0]?.attributes || [];
                    const attr = attrs.find(
                        (a: any) => a.name === facetKey,
                    );

                    return attr && values.includes(String(attr.value));
                },
            );
        });
    }, [sortedProducts, activeFilters]);

    // -----------------------------
    // EMPTY STATE
    // -----------------------------
    if (!products.length) {
        return <div className="min-h-[60vh]">

            <div className="max-w-6xl mx-auto px-6 pt-10">
                <div className="bg-[#efe4cc] border border-[#e0d3b0] rounded-sm px-6 py-4 flex items-center gap-3">

                    {/* Icon */}
                    <div className="w-5 h-5 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs">
                        !
                    </div>

                    {/* Message */}
                    <p className="text-sm text-[#6b5c3d]">
                        We can't find products matching the selection.
                    </p>

                </div>
            </div>

        </div>;
    }

    // -----------------------------
    // RENDER
    // -----------------------------
    return (
        <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold">Product Listing</h1>

                {/* SORT */}
                <select
                    value={sort}
                    onChange={(e) =>
                        setSort(
                            e.target.value as
                            | 'name-asc'
                            | 'name-desc'
                            | 'price-asc'
                            | 'price-desc',
                        )
                    }
                    className="border px-3 py-2 text-sm rounded"
                >
                    <option value="name-asc">Name (A–Z)</option>
                    <option value="name-desc">Name (Z–A)</option>
                    <option value="price-asc">
                        Price (Low → High)
                    </option>
                    <option value="price-desc">
                        Price (High → Low)
                    </option>
                </select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* FILTERS */}
                <aside className="space-y-6">
                    {facets.map((facet: any) => {
                        if (
                            facet.type !== 'term' ||
                            !facet.terms?.length
                        )
                            return null;

                        return (
                            <div key={facet.key}>
                                <h4 className="font-medium mb-2">
                                    {facet.label}
                                </h4>

                                {facet.terms.map((term: any) => (
                                    <label
                                        key={term.key}
                                        className="flex items-center gap-2 text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                activeFilters[
                                                    facet.key
                                                ]?.includes(term.key) ||
                                                false
                                            }
                                            onChange={() =>
                                                toggleFilter(
                                                    facet.key,
                                                    term.key,
                                                )
                                            }
                                        />
                                        {term.label}
                                    </label>
                                ))}
                            </div>
                        );
                    })}
                </aside>

                {/* PRODUCTS */}
                <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {filteredProducts.map((product: any) => {
                        const variant = product.variants?.[0];
                        const image = variant?.images?.[0];
                        const price = variant?.price;

                        const pageBasePath = '/prowhip-home';
                        const categorySlug =
                            product.categories?.[0]?.slug ||
                            'eshop';
                        const productUrl = `${pageBasePath}/${categorySlug}${product._url}`;

                        return (
                            <a
                                key={product.productId}
                                href={productUrl}
                                className="border rounded-lg p-4 hover:shadow-lg transition"
                            >
                                {image && (
                                    <img
                                        src={image}
                                        alt={product.name}
                                        className="w-full h-48 object-contain mb-3"
                                    />
                                )}

                                <h2 className="text-sm font-medium">
                                    {product.name}
                                </h2>

                                {price && (
                                    <p className="text-sm text-gray-700 mt-1">
                                        $
                                        {(price.centAmount / 100).toFixed(
                                            2,
                                        )}{' '}
                                        {price.currencyCode}
                                    </p>
                                )}
                            </a>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ProWhipProductList;
